import React from 'react';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from 'screens/Admin/Analytics/components/TimeRangeDropdown';
import './FunnelChart.css';

export interface FunnelStep {
  label: string;
  count: number;
  percentOfStart: number;
  dropFromPrev: number | null;
}

interface Props {
  title: string;
  subtitle?: string;
  steps: FunnelStep[];
  totalCohortSize: number;
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
  isLoading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  color?: string;
}

const FunnelChart: React.FC<Props> = ({
  title,
  subtitle,
  steps,
  totalCohortSize,
  period,
  onPeriodChange,
  isLoading,
  error,
  onRetry,
  color = '#8B5CF6'
}) => {
  const topCount = steps.length ? Math.max(...steps.map((s) => s.count), 1) : 1;
  const completed = steps.length ? steps[steps.length - 1].count : 0;
  const overallPct = totalCohortSize > 0 ? Math.round((completed / totalCohortSize) * 1000) / 10 : 0;

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>{title}</h2>
          {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        </div>
        <div className="chart-header-right">
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">
            Failed to load data
            {onRetry && (
              <button className="chart-retry-btn" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        ) : steps.length ? (
          <div className="funnel">
            {steps.map((step, i) => {
              const widthPct = Math.max((step.count / topCount) * 100, 4);
              const dropped = i > 0 ? steps[i - 1].count - step.count : 0;
              return (
                <React.Fragment key={step.label}>
                  {i > 0 && step.dropFromPrev != null && (
                    <div className="funnel-drop">
                      −{step.dropFromPrev}% ({dropped.toLocaleString()} users)
                    </div>
                  )}
                  <div className="funnel-step">
                    <div
                      className="funnel-bar"
                      style={{ width: `${widthPct}%`, backgroundColor: color }}
                    >
                      <span className="funnel-bar-label">{step.label}</span>
                      <span className="funnel-bar-value">
                        {step.count.toLocaleString()} ({step.percentOfStart}%)
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        ) : null}
      </div>

      {steps.length > 0 && (
        <div className="chart-summary">
          Overall conversion: {overallPct}% ({completed.toLocaleString()} of{' '}
          {totalCohortSize.toLocaleString()} users completed onboarding)
        </div>
      )}
    </div>
  );
};

export default FunnelChart;
